<template>
  <div
    class="flex w-full space-x-1"
    :class="{
      [`hide-increment`]: !!hideIncrement,
    }"
  >
    <slot />
    <CInputGroup
      type="text"
      :class="{ 'min-w-[4rem] max-w-[6rem]': stretch, 'w-16': !stretch }"
      name="quantity"
      :value="'' + count"
      pattern="[0-9]*"
      inputmode="numeric"
      :placeholder="placeholder"
      @update:model-value="handleInput($event)"
      @keydown="$emit('keydown', $event)"
      @blur="handleBlur()"
    />
    <CIconButton
      v-if="hideIncrement !== true"
      :class="buttonClass"
      name="remove"
      variant="secondary"
      size="medium"
      shape="round"
      :is-disabled="isDisabled"
      @click="decrement()"
    />
    <CIconButton
      v-if="hideIncrement !== true"
      :class="buttonClass"
      name="add"
      variant="secondary"
      size="medium"
      shape="round"
      @click="increment()"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import CInputGroup from '../../molecules/CInputGroup'
import CIconButton from '../../molecules/CIconButton'
import { debounce } from '../../../utilities/debounce'
import { formatFloat } from '../../../utilities/formatFloat'

const count = ref(0)

interface Props {
  size: 'mini' | 'small' | 'medium' | 'large'
  initialValue?: number
  multiple?: number
  hideIncrement?: boolean
  placeholder?: string
  buttonClass?: string
  stretch?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  initialValue: 0,
  multiple: 1,
  hideIncrement: false,
  placeholder: '',
  buttonClass: '',
  stretch: false,
})

const emit = defineEmits<{
  (e: 'countChange', value: number): void
  (e: 'waiting', value: boolean): void
  (e: 'keydown', event: KeyboardEvent): void
}>()

const increment = () => {
  count.value = formatFloat(count.value + props.multiple)
  emit('countChange', count.value)
}

const decrement = () => {
  if (count.value <= props.multiple) {
    return
  }
  count.value = formatFloat(count.value - props.multiple)
  emit('countChange', count.value)
}

const calculateQuantityWithMultiple = (value: number) => {
  count.value = formatFloat(
    Math.max(Math.ceil(value / props.multiple), 1) * props.multiple,
  )
}

const setInputValue = (value: string) => {
  const parsedValue = parseFloat(value.replaceAll(',', '.'))
  if (isNaN(parsedValue)) {
    count.value = props.multiple
  } else {
    calculateQuantityWithMultiple(parsedValue)
  }
  emit('countChange', count.value)
  if (props.multiple !== 1) {
    emit('waiting', false)
  }
}

const handleBlur = () => {
  if (!count.value) {
    calculateQuantityWithMultiple(props.initialValue)
  }
}

const handleInput = (value: string) => {
  count.value = Number(value)
  if (!value) return
  if (props.multiple !== 1) {
    emit('waiting', true)
    debounceInput(value)
    return
  }
  setInputValue(value)
}

const debounceInput = debounce((value: string) => {
  setInputValue(value)
}, 800)

const isDisabled = computed(() => {
  return count.value <= props.multiple
})

watch(
  () => props.initialValue,
  (value) => {
    calculateQuantityWithMultiple(value)
  },
)

watch(
  () => props.multiple,
  () => {
    calculateQuantityWithMultiple(props.initialValue)
  },
)

calculateQuantityWithMultiple(props.initialValue)
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CCounterNew',
})
</script>
